import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangleIcon, ShieldIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, DownloadIcon, CheckIcon, BoxIcon, UserIcon, RefreshCwIcon, FileTextIcon, GlobeIcon, PackageIcon, ClipboardListIcon, TrendingUpIcon, TrendingDownIcon, DollarSignIcon, InfoIcon, BarChart2Icon, ListIcon, ArrowRightIcon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import LineChart from '../components/charts/LineChart';
import RechartsLineChart from '../components/charts/RechartsLineChart';
const RiskScoreDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Sample risk assessment data (in production would come from API)
  const [riskData, setRiskData] = useState({
    overall_risk_score: 47.25,
    risk_category: 'MEDIUM',
    threshold: {
      low: 30,
      medium: 60,
      high: 85
    },
    component_scores: {
      supplier_risk: 38.4,
      device_verification: 52.7,
      device_authenticity: 25.3,
      transaction_risk: 61.5,
      compliance_risk: 42.8
    },
    weighted_scores: {
      supplier_risk: 9.6,
      device_verification: 15.81,
      device_authenticity: 5.06,
      transaction_risk: 9.23,
      compliance_risk: 4.28
    },
    industry_benchmarks: {
      supplier_risk: 25.0,
      device_verification: 40.0,
      device_authenticity: 22.0,
      transaction_risk: 45.0,
      compliance_risk: 30.0
    },
    historical_trends: [{
      date: 'Feb',
      value: 52.8
    }, {
      date: 'Mar',
      value: 54.3
    }, {
      date: 'Apr',
      value: 51.2
    }, {
      date: 'May',
      value: 49.6
    }, {
      date: 'Jun',
      value: 48.1
    }, {
      date: 'Jul',
      value: 47.25
    }],
    recommendation: {
      action: 'ADDITIONAL_VERIFICATION',
      details: 'Proceed with additional verification of highest risk components.',
      required_approvals: ['verification_team'],
      specific_actions: [{
        component: 'transaction_risk',
        action: 'Verify payment method and buyer history',
        severity: 'high'
      }, {
        component: 'device_verification',
        action: 'Run extended diagnostics on device connectivity',
        severity: 'medium'
      }, {
        component: 'compliance_risk',
        action: 'Confirm export eligibility for destination market',
        severity: 'low'
      }]
    },
    financial_impact: {
      estimated_cost: 120,
      potential_loss: 410,
      roi: 241.7
    }
  });

  // Device information
  const deviceInfo = {
    imei: '358673102468313',
    model: 'iPhone 13 Pro',
    storage: '256GB',
    carrier: 'Verizon',
    grade: 'B+',
    supplier: 'TechTrade Solutions',
    market_value: '$450-480',
    blacklist_status: 'Clean',
    activation_lock: 'Off',
    repair_history: 'Screen replacement (OEM)'
  };

  // Supplier information
  const supplierInfo = {
    name: 'TechTrade Solutions',
    relationship_length: '1.8 years',
    transaction_count: 284,
    return_rate: '3.2%',
    average_quality: 'B+',
    payment_terms: 'Net 30',
    location: 'Miami, FL',
    verification_status: 'Verified'
  };

  // Function to determine color based on risk score
  const getRiskColor = (score, type = 'bg') => {
    if (score <= 30) return type === 'bg' ? 'bg-green-100 text-green-800' : 'text-green-600';
    if (score <= 60) return type === 'bg' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-600';
    if (score <= 85) return type === 'bg' ? 'bg-orange-100 text-orange-800' : 'text-orange-600';
    return type === 'bg' ? 'bg-red-100 text-red-800' : 'text-red-600';
  };

  // Function to determine badge variant based on risk category
  const getRiskBadgeVariant = category => {
    switch (category) {
      case 'LOW':
        return 'success';
      case 'MEDIUM':
        return 'warning';
      case 'HIGH':
        return 'destructive';
      case 'CRITICAL':
        return 'destructive';
      default:
        return 'default';
    }
  };

  // Function to determine action button style
  const getActionStyle = action => {
    switch (action) {
      case 'PROCEED':
        return 'bg-green-500 text-white';
      case 'ADDITIONAL_VERIFICATION':
        return 'bg-yellow-500 text-white';
      case 'MANUAL_REVIEW':
        return 'bg-orange-500 text-white';
      case 'REJECT':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Function to get icon for each risk component
  const getRiskComponentIcon = component => {
    switch (component) {
      case 'supplier_risk':
        return <UserIcon className="h-4 w-4" data-id="element-1653" />;
      case 'device_verification':
        return <ShieldIcon className="h-4 w-4" data-id="element-1654" />;
      case 'device_authenticity':
        return <CheckCircleIcon className="h-4 w-4" data-id="element-1655" />;
      case 'transaction_risk':
        return <RefreshCwIcon className="h-4 w-4" data-id="element-1656" />;
      case 'compliance_risk':
        return <FileTextIcon className="h-4 w-4" data-id="element-1657" />;
      default:
        return <AlertCircleIcon className="h-4 w-4" data-id="element-1658" />;
    }
  };

  // Function to get trend indicator
  const getTrendIndicator = (current, benchmark) => {
    if (current > benchmark) {
      return <span className="flex items-center text-red-500 text-xs ml-1" data-id="element-1659">
          <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-1660" />
          {((current - benchmark) / benchmark * 100).toFixed(1)}%
        </span>;
    } else {
      return <span className="flex items-center text-green-500 text-xs ml-1" data-id="element-1661">
          <TrendingDownIcon className="h-3 w-3 mr-1" data-id="element-1662" />
          {((benchmark - current) / benchmark * 100).toFixed(1)}%
        </span>;
    }
  };

  // Function to get severity badge
  const getSeverityBadge = severity => {
    switch (severity) {
      case 'high':
        return <Badge variant="destructive" data-id="element-1663">High</Badge>;
      case 'medium':
        return <Badge variant="warning" data-id="element-1664">Medium</Badge>;
      case 'low':
        return <Badge variant="success" data-id="element-1665">Low</Badge>;
      default:
        return <Badge data-id="element-1666">Unknown</Badge>;
    }
  };
  return <div className="space-y-6" data-id="element-1667">
      <div className="flex items-center justify-between" data-id="element-1668">
        <div data-id="element-1669">
          <h1 className="text-2xl font-bold" data-id="element-1670">Risk Assessment</h1>
          <p className="text-sm text-muted-foreground" data-id="element-1671">
            Device IMEI: {deviceInfo.imei} | Model: {deviceInfo.model} |
            Supplier: {deviceInfo.supplier}
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-1672">
          <Button variant="outline" data-id="element-1673">
            <DownloadIcon className="mr-2 h-4 w-4" data-id="element-1674" />
            Export Report
          </Button>
          <Button data-id="element-1675">
            <CheckIcon className="mr-2 h-4 w-4" data-id="element-1676" />
            Submit for Approval
          </Button>
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-id="element-1677">
        <TabsList className="grid grid-cols-4 w-full" data-id="element-1678">
          <TabsTrigger value="overview" data-id="element-1679">Overview</TabsTrigger>
          <TabsTrigger value="details" data-id="element-1680">Detailed Analysis</TabsTrigger>
          <TabsTrigger value="actions" data-id="element-1681">Recommended Actions</TabsTrigger>
          <TabsTrigger value="history" data-id="element-1682">History & Trends</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4" data-id="element-1683">
          {/* Overall Risk Score */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1684">
            <Card data-id="element-1685">
              <CardHeader className="pb-2" data-id="element-1686">
                <CardTitle data-id="element-1687">Overall Risk Score</CardTitle>
                <CardDescription data-id="element-1688">
                  Weighted composite risk assessment
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-1689">
                <div className="flex items-center justify-center h-32" data-id="element-1690">
                  <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${getRiskColor(riskData.overall_risk_score)}`} data-id="element-1691">
                    <div className="flex flex-col items-center" data-id="element-1692">
                      <span className="text-3xl font-bold" data-id="element-1693">
                        {riskData.overall_risk_score}
                      </span>
                      <span className="text-xs mt-1" data-id="element-1694">
                        Threshold: {riskData.threshold.low}-
                        {riskData.threshold.medium}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center" data-id="element-1695">
                  <Badge variant={getRiskBadgeVariant(riskData.risk_category)} data-id="element-1696">
                    {riskData.risk_category}
                  </Badge>
                  <div className="flex items-center justify-center mt-2 text-sm text-muted-foreground" data-id="element-1697">
                    <TrendingDownIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-1698" />
                    <span data-id="element-1699">-8.2% from 30 days ago</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Recommendation */}
            <Card data-id="element-1700">
              <CardHeader className="pb-2" data-id="element-1701">
                <CardTitle data-id="element-1702">Recommended Action</CardTitle>
                <CardDescription data-id="element-1703">Based on risk assessment</CardDescription>
              </CardHeader>
              <CardContent data-id="element-1704">
                <div className={`p-4 rounded ${getActionStyle(riskData.recommendation.action)}`} data-id="element-1705">
                  <div className="text-lg font-bold mb-2" data-id="element-1706">
                    {riskData.recommendation.action.replace('_', ' ')}
                  </div>
                  <p className="text-sm" data-id="element-1707">{riskData.recommendation.details}</p>
                </div>
                <div className="mt-4" data-id="element-1708">
                  <p className="text-sm font-medium mb-1" data-id="element-1709">
                    Top Priority Action:
                  </p>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800 flex items-start" data-id="element-1710">
                    <AlertTriangleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1711" />
                    <div data-id="element-1712">
                      <p className="text-sm font-medium" data-id="element-1713">
                        {riskData.recommendation.specific_actions[0].action}
                      </p>
                      <div className="flex items-center mt-1" data-id="element-1714">
                        {getSeverityBadge(riskData.recommendation.specific_actions[0].severity)}
                        <span className="text-xs text-muted-foreground ml-2" data-id="element-1715">
                          Transaction Risk:{' '}
                          {riskData.component_scores.transaction_risk}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {riskData.recommendation.required_approvals.length > 0 && <div className="mt-4" data-id="element-1716">
                    <p className="text-sm font-medium mb-1" data-id="element-1717">
                      Required Approvals:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-1718">
                      {riskData.recommendation.required_approvals.map(approver => <Badge key={approver} variant="outline" className="capitalize" data-id="element-1719">
                            {approver.replace('_', ' ')}
                          </Badge>)}
                    </div>
                  </div>}
              </CardContent>
            </Card>
            {/* Financial Impact */}
            <Card data-id="element-1720">
              <CardHeader className="pb-2" data-id="element-1721">
                <CardTitle data-id="element-1722">Financial Impact</CardTitle>
                <CardDescription data-id="element-1723">Cost-benefit analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-1724">
                <div className="flex justify-between items-center pb-2 border-b" data-id="element-1725">
                  <span className="text-sm text-muted-foreground" data-id="element-1726">
                    Verification Cost
                  </span>
                  <span className="font-medium" data-id="element-1727">
                    ${riskData.financial_impact.estimated_cost}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b" data-id="element-1728">
                  <span className="text-sm text-muted-foreground" data-id="element-1729">
                    Potential Loss Prevention
                  </span>
                  <span className="font-medium text-green-600" data-id="element-1730">
                    ${riskData.financial_impact.potential_loss}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1731">
                  <span className="text-sm text-muted-foreground" data-id="element-1732">
                    ROI of Verification
                  </span>
                  <span className="font-medium text-green-600" data-id="element-1733">
                    {riskData.financial_impact.roi}%
                  </span>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800" data-id="element-1734">
                  <div className="flex items-start" data-id="element-1735">
                    <InfoIcon className="h-4 w-4 text-blue-500 mr-2 mt-0.5" data-id="element-1736" />
                    <p className="text-xs" data-id="element-1737">
                      Proceeding with verification steps is recommended based on
                      financial analysis.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Component Risk Scores */}
          <Card data-id="element-1738">
            <CardHeader className="pb-2" data-id="element-1739">
              <CardTitle data-id="element-1740">Risk Component Breakdown</CardTitle>
              <CardDescription data-id="element-1741">
                Individual risk factors and their contributions
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-1742">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-1743">
                {Object.entries(riskData.component_scores).map(([key, value]) => {
                const benchmark = riskData.industry_benchmarks[key];
                return <div key={key} className="flex flex-col items-center" data-id="element-1744">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 border-2 ${getRiskColor(value)}`} data-id="element-1745">
                          <div className="flex flex-col items-center" data-id="element-1746">
                            {getRiskComponentIcon(key)}
                            <span className="text-lg font-bold mt-1" data-id="element-1747">
                              {value}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-center capitalize" data-id="element-1748">
                          {key.replace('_', ' ')}
                        </span>
                        <div className="flex items-center text-xs text-muted-foreground" data-id="element-1749">
                          <span data-id="element-1750">Industry: {benchmark}</span>
                          {getTrendIndicator(value, benchmark)}
                        </div>
                      </div>;
              })}
              </div>
              {/* Risk Score Visualization */}
              <div className="mt-6" data-id="element-1751">
                <p className="text-sm font-medium mb-2" data-id="element-1752">
                  Risk Score Contribution
                </p>
                <div className="h-6 flex rounded-md overflow-hidden" data-id="element-1753">
                  {Object.entries(riskData.weighted_scores).map(([key, value]) => {
                  const score = riskData.component_scores[key];
                  return <div key={key} className={`${getRiskColor(score)}`} style={{
                    width: `${value / riskData.overall_risk_score * 100}%`
                  }} title={`${key.replace('_', ' ')}: ${value} points (${(value / riskData.overall_risk_score * 100).toFixed(1)}%)`} data-id="element-1754" />;
                })}
                </div>
                <div className="flex text-xs text-muted-foreground mt-1 justify-between" data-id="element-1755">
                  <span data-id="element-1756">0</span>
                  <span data-id="element-1757">25</span>
                  <span data-id="element-1758">50</span>
                  <span data-id="element-1759">75</span>
                  <span data-id="element-1760">100</span>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Next Steps */}
          <Card data-id="element-1761">
            <CardHeader className="pb-2" data-id="element-1762">
              <CardTitle data-id="element-1763">Integration with Workflow</CardTitle>
              <CardDescription data-id="element-1764">
                Next steps based on risk assessment
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-1765">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1766">
                <Button variant="outline" className="h-auto p-4 flex flex-col items-center justify-center space-y-2" onClick={() => navigate('/inventory')} data-id="element-1767">
                  <PackageIcon className="h-8 w-8 mb-2 text-blue-500" data-id="element-1768" />
                  <span className="font-medium" data-id="element-1769">Add to Inventory</span>
                  <span className="text-xs text-muted-foreground text-center" data-id="element-1770">
                    Add this device to your inventory with risk notations
                  </span>
                </Button>
                <Button variant="outline" className="h-auto p-4 flex flex-col items-center justify-center space-y-2" onClick={() => navigate('/order-management')} data-id="element-1771">
                  <ClipboardListIcon className="h-8 w-8 mb-2 text-green-500" data-id="element-1772" />
                  <span className="font-medium" data-id="element-1773">Process Order</span>
                  <span className="text-xs text-muted-foreground text-center" data-id="element-1774">
                    Continue with order processing workflow
                  </span>
                </Button>
                <Button variant="outline" className="h-auto p-4 flex flex-col items-center justify-center space-y-2" onClick={() => navigate('/phone-grading')} data-id="element-1775">
                  <RefreshCwIcon className="h-8 w-8 mb-2 text-orange-500" data-id="element-1776" />
                  <span className="font-medium" data-id="element-1777">Return to Grading</span>
                  <span className="text-xs text-muted-foreground text-center" data-id="element-1778">
                    Perform additional device verification
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="details" className="space-y-4" data-id="element-1779">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1780">
            {/* Device Details */}
            <Card data-id="element-1781">
              <CardHeader data-id="element-1782">
                <CardTitle data-id="element-1783">Device Details</CardTitle>
                <CardDescription data-id="element-1784">
                  Comprehensive device information
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-1785">
                <div className="space-y-3" data-id="element-1786">
                  {Object.entries(deviceInfo).map(([key, value]) => <div key={key} className="flex justify-between items-center pb-2 border-b" data-id="element-1787">
                      <span className="text-sm text-muted-foreground capitalize" data-id="element-1788">
                        {key.replace('_', ' ')}:
                      </span>
                      <span className="font-medium" data-id="element-1789">{value}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
            {/* Supplier Details */}
            <Card data-id="element-1790">
              <CardHeader data-id="element-1791">
                <CardTitle data-id="element-1792">Supplier Profile</CardTitle>
                <CardDescription data-id="element-1793">Supplier risk assessment data</CardDescription>
              </CardHeader>
              <CardContent data-id="element-1794">
                <div className="space-y-3" data-id="element-1795">
                  {Object.entries(supplierInfo).map(([key, value]) => <div key={key} className="flex justify-between items-center pb-2 border-b" data-id="element-1796">
                      <span className="text-sm text-muted-foreground capitalize" data-id="element-1797">
                        {key.replace('_', ' ')}:
                      </span>
                      <span className="font-medium" data-id="element-1798">{value}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Detailed Risk Components */}
          <Card data-id="element-1799">
            <CardHeader data-id="element-1800">
              <CardTitle data-id="element-1801">Detailed Risk Analysis</CardTitle>
              <CardDescription data-id="element-1802">
                In-depth assessment of each risk component
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6" data-id="element-1803">
              {/* Supplier Risk Details */}
              <div className="p-4 rounded-md border" data-id="element-1804">
                <div className="flex items-center mb-3" data-id="element-1805">
                  <UserIcon className="h-5 w-5 mr-2" data-id="element-1806" />
                  <h3 className="font-medium" data-id="element-1807">
                    Supplier Risk ({riskData.component_scores.supplier_risk})
                  </h3>
                  <Badge className="ml-2" variant={riskData.component_scores.supplier_risk <= 30 ? 'success' : riskData.component_scores.supplier_risk <= 60 ? 'warning' : 'destructive'} data-id="element-1808">
                    {riskData.component_scores.supplier_risk <= 30 ? 'Low' : riskData.component_scores.supplier_risk <= 60 ? 'Medium' : 'High'}
                  </Badge>
                </div>
                <div className="space-y-2 text-sm" data-id="element-1809">
                  <p className="text-muted-foreground" data-id="element-1810">
                    This supplier has completed 284 transactions with a 3.2%
                    return rate, which is slightly above the industry average of
                    2.8%.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-1811">
                    <div className="p-2 bg-muted rounded-md" data-id="element-1812">
                      <span className="block text-xs text-muted-foreground" data-id="element-1813">
                        Return Rate
                      </span>
                      <span className="font-medium" data-id="element-1814">3.2%</span>
                      <span className="text-xs text-red-500 ml-1" data-id="element-1815">+0.4%</span>
                    </div>
                    <div className="p-2 bg-muted rounded-md" data-id="element-1816">
                      <span className="block text-xs text-muted-foreground" data-id="element-1817">
                        Avg. Quality
                      </span>
                      <span className="font-medium" data-id="element-1818">B+</span>
                      <span className="text-xs text-green-500 ml-1" data-id="element-1819">+0.5</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Transaction Risk Details */}
              <div className="p-4 rounded-md border" data-id="element-1820">
                <div className="flex items-center mb-3" data-id="element-1821">
                  <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-1822" />
                  <h3 className="font-medium" data-id="element-1823">
                    Transaction Risk (
                    {riskData.component_scores.transaction_risk})
                  </h3>
                  <Badge className="ml-2" variant="destructive" data-id="element-1824">
                    High
                  </Badge>
                </div>
                <div className="space-y-2 text-sm" data-id="element-1825">
                  <p className="text-muted-foreground" data-id="element-1826">
                    The transaction pattern shows unusual characteristics that
                    require additional verification.
                  </p>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800" data-id="element-1827">
                    <div className="flex items-start" data-id="element-1828">
                      <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5" data-id="element-1829" />
                      <div data-id="element-1830">
                        <p className="text-sm font-medium" data-id="element-1831">High Risk Flags</p>
                        <ul className="mt-1 space-y-1 list-disc list-inside text-xs text-muted-foreground" data-id="element-1832">
                          <li data-id="element-1833">Unusual payment method for transaction size</li>
                          <li data-id="element-1834">Buyer location differs from shipping address</li>
                          <li data-id="element-1835">
                            Rapid transaction request (less than 24 hours)
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Device Verification Risk Details */}
              <div className="p-4 rounded-md border" data-id="element-1836">
                <div className="flex items-center mb-3" data-id="element-1837">
                  <ShieldIcon className="h-5 w-5 mr-2" data-id="element-1838" />
                  <h3 className="font-medium" data-id="element-1839">
                    Device Verification (
                    {riskData.component_scores.device_verification})
                  </h3>
                  <Badge className="ml-2" variant="warning" data-id="element-1840">
                    Medium
                  </Badge>
                </div>
                <div className="space-y-2 text-sm" data-id="element-1841">
                  <p className="text-muted-foreground" data-id="element-1842">
                    Device verification shows some potential issues that should
                    be addressed.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-1843">
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800" data-id="element-1844">
                      <span className="block text-xs font-medium" data-id="element-1845">
                        Passed Checks
                      </span>
                      <ul className="mt-1 space-y-1 list-disc list-inside text-xs text-muted-foreground" data-id="element-1846">
                        <li data-id="element-1847">IMEI validation</li>
                        <li data-id="element-1848">Activation lock</li>
                        <li data-id="element-1849">Hardware diagnostics</li>
                      </ul>
                    </div>
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800" data-id="element-1850">
                      <span className="block text-xs font-medium" data-id="element-1851">
                        Failed Checks
                      </span>
                      <ul className="mt-1 space-y-1 list-disc list-inside text-xs text-muted-foreground" data-id="element-1852">
                        <li data-id="element-1853">Wi-Fi connectivity test</li>
                        <li data-id="element-1854">Complete battery health verification</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="actions" className="space-y-4" data-id="element-1855">
          <Card data-id="element-1856">
            <CardHeader data-id="element-1857">
              <CardTitle data-id="element-1858">Recommended Actions</CardTitle>
              <CardDescription data-id="element-1859">
                Steps to mitigate identified risks
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-1860">
              <div className="space-y-4" data-id="element-1861">
                {riskData.recommendation.specific_actions.map((action, index) => <div key={index} className="flex items-start p-4 border rounded-md" data-id="element-1862">
                      <div className="flex-shrink-0 mr-3 mt-1" data-id="element-1863">
                        {action.severity === 'high' ? <AlertTriangleIcon className="h-5 w-5 text-red-500" data-id="element-1864" /> : action.severity === 'medium' ? <AlertCircleIcon className="h-5 w-5 text-yellow-500" data-id="element-1865" /> : <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-1866" />}
                      </div>
                      <div className="flex-1" data-id="element-1867">
                        <div className="flex items-center mb-2" data-id="element-1868">
                          <h3 className="font-medium" data-id="element-1869">{action.action}</h3>
                          <div className="ml-2" data-id="element-1870">
                            {getSeverityBadge(action.severity)}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3" data-id="element-1871">
                          Related to {action.component.replace('_', ' ')} score
                          of {riskData.component_scores[action.component]}
                        </p>
                        <Button size="sm" className="flex items-center" data-id="element-1872">
                          Take Action{' '}
                          <ArrowRightIcon className="ml-1 h-3 w-3" data-id="element-1873" />
                        </Button>
                      </div>
                    </div>)}
                <div className="p-4 border rounded-md bg-blue-50 dark:bg-blue-900/20" data-id="element-1874">
                  <h3 className="font-medium flex items-center mb-2" data-id="element-1875">
                    <InfoIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-1876" />
                    Integration with Workflow
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3" data-id="element-1877">
                    This risk assessment should be integrated at the following
                    points in your workflow:
                  </p>
                  <ul className="space-y-2 text-sm" data-id="element-1878">
                    <li className="flex items-start" data-id="element-1879">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5" data-id="element-1880" />
                      <span data-id="element-1881">
                        <strong data-id="element-1882">Pre-purchase verification:</strong> Validate
                        supplier and device before acquisition
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1883">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5" data-id="element-1884" />
                      <span data-id="element-1885">
                        <strong data-id="element-1886">Post-grading assessment:</strong> Confirm device
                        meets compliance requirements
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1887">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5" data-id="element-1888" />
                      <span data-id="element-1889">
                        <strong data-id="element-1890">Pre-sale verification:</strong> Validate buyer
                        and transaction details
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1891">
            <CardHeader data-id="element-1892">
              <CardTitle data-id="element-1893">Workflow Decision Points</CardTitle>
              <CardDescription data-id="element-1894">
                Risk assessment integration in your process
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-1895">
              <div className="relative" data-id="element-1896">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-muted" data-id="element-1897"></div>
                <div className="space-y-6" data-id="element-1898">
                  <div className="relative pl-8" data-id="element-1899">
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs" data-id="element-1900">
                      1
                    </div>
                    <div data-id="element-1901">
                      <h3 className="font-medium" data-id="element-1902">Early Acquisition Phase</h3>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1903">
                        Pre-emptive risk assessment to avoid high-risk inventory
                        acquisition
                      </p>
                      <div className="mt-2 flex space-x-2" data-id="element-1904">
                        <Badge variant="outline" data-id="element-1905">Supplier Risk</Badge>
                        <Badge variant="outline" data-id="element-1906">Transaction Risk</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-8" data-id="element-1907">
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs" data-id="element-1908">
                      2
                    </div>
                    <div data-id="element-1909">
                      <h3 className="font-medium" data-id="element-1910">
                        Between IMEI Verification and Unlocking
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1911">
                        Comprehensive device verification and authenticity
                        validation
                      </p>
                      <div className="mt-2 flex space-x-2" data-id="element-1912">
                        <Badge variant="outline" data-id="element-1913">Device Verification</Badge>
                        <Badge variant="outline" data-id="element-1914">Device Authenticity</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-8" data-id="element-1915">
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs" data-id="element-1916">
                      3
                    </div>
                    <div data-id="element-1917">
                      <h3 className="font-medium" data-id="element-1918">Pre-Market Selection</h3>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1919">
                        Ensure devices meet compliance requirements for target
                        markets
                      </p>
                      <div className="mt-2 flex space-x-2" data-id="element-1920">
                        <Badge variant="outline" data-id="element-1921">Compliance Risk</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-8" data-id="element-1922">
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs" data-id="element-1923">
                      4
                    </div>
                    <div data-id="element-1924">
                      <h3 className="font-medium" data-id="element-1925">Transaction Security</h3>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1926">
                        Transaction monitoring and fraud prevention
                      </p>
                      <div className="mt-2 flex space-x-2" data-id="element-1927">
                        <Badge variant="outline" data-id="element-1928">Transaction Risk</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="history" className="space-y-4" data-id="element-1929">
          <Card data-id="element-1930">
            <CardHeader data-id="element-1931">
              <CardTitle data-id="element-1932">Risk Score History</CardTitle>
              <CardDescription data-id="element-1933">6-month historical trend</CardDescription>
            </CardHeader>
            <CardContent data-id="element-1934">
              <div className="h-60" data-id="element-1935">
                <RechartsLineChart data={riskData.historical_trends} height={200} color="#3b82f6" yAxisFormatter={value => `${value}`} tooltipFormatter={value => [`${value}`, 'Risk Score']} data-id="element-1936" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-1937">
                <div className="p-4 bg-muted/30 rounded-md" data-id="element-1938">
                  <p className="text-sm font-medium mb-1" data-id="element-1939">
                    Risk Trend (30 days)
                  </p>
                  <div className="flex items-end" data-id="element-1940">
                    <span className="text-xl font-bold text-green-600" data-id="element-1941">
                      -8.2%
                    </span>
                    <span className="text-xs text-muted-foreground ml-2" data-id="element-1942">
                      Decreasing risk
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-muted/30 rounded-md" data-id="element-1943">
                  <p className="text-sm font-medium mb-1" data-id="element-1944">
                    Highest Risk Factor
                  </p>
                  <div className="flex items-end" data-id="element-1945">
                    <span className="text-xl font-bold text-orange-600" data-id="element-1946">
                      Transaction Risk
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-muted/30 rounded-md" data-id="element-1947">
                  <p className="text-sm font-medium mb-1" data-id="element-1948">Similar Devices</p>
                  <div className="flex items-end" data-id="element-1949">
                    <span className="text-xl font-bold" data-id="element-1950">42.1</span>
                    <span className="text-xs text-muted-foreground ml-2" data-id="element-1951">
                      Avg. risk score
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1952">
            <CardHeader data-id="element-1953">
              <CardTitle data-id="element-1954">Risk Assessment Log</CardTitle>
              <CardDescription data-id="element-1955">
                Previous assessments and actions
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-1956">
              <div className="space-y-4" data-id="element-1957">
                <div className="border rounded-md overflow-hidden" data-id="element-1958">
                  <table className="w-full" data-id="element-1959">
                    <thead className="bg-muted/50" data-id="element-1960">
                      <tr data-id="element-1961">
                        <th className="text-left py-2 px-4 font-medium text-muted-foreground" data-id="element-1962">
                          Date
                        </th>
                        <th className="text-left py-2 px-4 font-medium text-muted-foreground" data-id="element-1963">
                          Score
                        </th>
                        <th className="text-left py-2 px-4 font-medium text-muted-foreground" data-id="element-1964">
                          Category
                        </th>
                        <th className="text-left py-2 px-4 font-medium text-muted-foreground" data-id="element-1965">
                          Action Taken
                        </th>
                        <th className="text-left py-2 px-4 font-medium text-muted-foreground" data-id="element-1966">
                          User
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1967">
                      <tr className="border-t" data-id="element-1968">
                        <td className="py-3 px-4" data-id="element-1969">Jul 15, 2023</td>
                        <td className="py-3 px-4" data-id="element-1970">47.25</td>
                        <td className="py-3 px-4" data-id="element-1971">
                          <Badge variant="warning" data-id="element-1972">MEDIUM</Badge>
                        </td>
                        <td className="py-3 px-4" data-id="element-1973">Additional Verification</td>
                        <td className="py-3 px-4" data-id="element-1974">John Doe</td>
                      </tr>
                      <tr className="border-t" data-id="element-1975">
                        <td className="py-3 px-4" data-id="element-1976">Jun 10, 2023</td>
                        <td className="py-3 px-4" data-id="element-1977">48.10</td>
                        <td className="py-3 px-4" data-id="element-1978">
                          <Badge variant="warning" data-id="element-1979">MEDIUM</Badge>
                        </td>
                        <td className="py-3 px-4" data-id="element-1980">Additional Verification</td>
                        <td className="py-3 px-4" data-id="element-1981">Sarah Chen</td>
                      </tr>
                      <tr className="border-t" data-id="element-1982">
                        <td className="py-3 px-4" data-id="element-1983">May 22, 2023</td>
                        <td className="py-3 px-4" data-id="element-1984">49.60</td>
                        <td className="py-3 px-4" data-id="element-1985">
                          <Badge variant="warning" data-id="element-1986">MEDIUM</Badge>
                        </td>
                        <td className="py-3 px-4" data-id="element-1987">Additional Verification</td>
                        <td className="py-3 px-4" data-id="element-1988">John Doe</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>;
};
export default RiskScoreDashboard;